import { useEffect, useState } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import {
  faBell,
  faCopyright,
  faEdit,
  faPencil
} from '@fortawesome/pro-regular-svg-icons';
import { Accordion, ButtonText, Checkbox, useToast } from '@morressier/alembic';
import { useForm } from 'react-hook-form';

import { Utils } from '@publishing-engine/editorial-form-field-build/src';
import { IEditorialFormField } from '@publishing-engine/domain/models';
import { IconBadge } from 'src/components/IconBadge';
import HighlightItem from '../PeerReview/HighlightItem';
import { useUpdateEditorialFormData } from 'src/api/commands';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import useEditorialForm from 'src/hooks/useEditorialForm';
import ProtectedAction from 'src/components/ProtectedAction';
import { ERRORS } from 'src/constants';
import { EditorialAuthorWithdrawalRequested } from '../common/WithdrawalRequested';
import { SubmitChangeRequest } from 'src/components/Submission/decisionModals/changeRequest/SubmitChangeRequest';
import { faRefresh } from '@fortawesome/pro-light-svg-icons';
import { ChangeRequestModal } from 'src/components/Submission/decisionModals/changeRequest/ChangeRequestLogModal';
import AdditionalInformationWrapper from '../../AdditionalInformationWrapper';
import { getCheckListName, pluralizeString } from 'src/utils/string';
import { SubmissionDetailTab } from 'src/pages/SubmissionDetails';
import { useSubmissionDetails } from 'src/pages/SubmissionDetails/SubmissionDetailsContext';
import ProtectedWorkflowAction from '../../../../ProtectedAction/ProtectedWorkflowAction';
import { getIcmjeDisclosureLabel } from '../../../../../utils/icmje';
import { BACKOFFICE_TABS } from '../../../BackofficeTab/BackofficeTab';
import { isFormConfigured } from '../../../../../utils/formConfig';
import { FieldLabel } from 'src/components/Field';

interface Props {
  setActiveTab: (tab: SubmissionDetailTab) => void;
}

export const EditorialPreProductionAdditionalInformation = ({
  setActiveTab
}: Props) => {
  const [showRequestUpdatesModal, setShowRequestUpdatesModal] = useState(false);
  const [showChangeRequestLog, setShowChangeRequestLog] = useState(false);

  const toast = useToast();
  const {
    submissionId,
    submissionContextId,
    changeRequestData,
    workflowResponse: { articleWorkflow },
    icmjeForm,
    icmjeFormList,
    organizationId,
    submissionContext
  } = useSubmissionData();
  const { setBackOfficeSubTab } = useSubmissionDetails();

  const fetchPreProductionDataKey = [
    'submission-checklist',
    {
      submissionId,
      phase: 'preProduction',
      articleWorkflowId: articleWorkflow.id
    }
  ];
  const updateChecklistMutation = useUpdateEditorialFormData(
    submissionId,
    submissionContextId,
    articleWorkflow.id,
    'preProduction',
    fetchPreProductionDataKey,
    organizationId
  );
  const {
    editorialFormSchema: preProductionFormSchema,
    editorialFormData: preProductionFormData,
    defaultFormValues
  } = useEditorialForm(
    submissionId,
    submissionContextId,
    articleWorkflow.organizationId,
    'preProduction',
    articleWorkflow.id
  );

  const { reset, register } = useForm<Record<string, boolean>>({
    defaultValues: defaultFormValues
  });

  useEffect(() => {
    if (preProductionFormData?.caseData) {
      reset({
        ...defaultFormValues,
        ...(preProductionFormData?.caseData as Record<string, boolean>)
      });
    }
  }, [preProductionFormData, reset]);

  const updateChecklist = async (
    name: string,
    value: boolean,
    label?: string
  ) => {
    const response = await updateChecklistMutation.mutateAsync({
      [name]: value
    });

    if (response.isSuccess) {
      const toastFieldName = getCheckListName(label || name);
      return toast({
        variant: 'success',
        title: `${toastFieldName} ${value ? 'checked' : 'unchecked'}`
      });
    } else {
      return toast({
        variant: 'error',
        title: ERRORS.somethingWentWrong
      });
    }
  };

  const renderCheckbox = (field: IEditorialFormField) => {
    const { name, component, params } = field;
    const { caseDataField, labels, validations } = params;

    const registerField = register(name);

    if (component !== 'Checkbox') {
      return null;
    }

    return (
      <Flex key={name} gap={2} flexDirection="column">
        <ProtectedAction
          relation="task_completer"
          entityType="journal_phase"
          entityId={`${submissionContextId}_preProduction`}
          tooltipLabel="You do not have permission to perform this action"
        >
          <Checkbox
            id={name}
            ariaLabel={labels.main}
            {...registerField}
            onChange={e => {
              registerField.onChange(e);
              updateChecklist(name, e.target.checked, labels.main);
            }}
            value={
              Utils.JsonUtil.extractByPath({
                json: preProductionFormData,
                path: caseDataField
              }) as string
            }
            isChecked={Boolean(
              Utils.JsonUtil.extractByPath({
                json: preProductionFormData,
                path: caseDataField
              })
            )}
            isRequired={validations?.required}
          >
            <FieldLabel
              isRequired={validations?.required}
              label={labels.main}
              subText={''}
              helpText={labels.description}
            />
          </Checkbox>
        </ProtectedAction>
      </Flex>
    );
  };

  const totalRequestedChanges =
    changeRequestData?.changeRequest?.requestHistory?.flatMap(
      history => history.fields
    ).length;

  const changeRequestCounts =
    changeRequestData?.changeRequest?.fields?.length ?? 0;
  const changeRequestTitle = `${changeRequestCounts} ${pluralizeString(
    'Unsent Request',
    changeRequestCounts
  )}`;

  return (
    <AdditionalInformationWrapper>
      {/* ----- Show Request Updates Modal ----- */}
      {showRequestUpdatesModal && (
        <SubmitChangeRequest
          onClose={() => setShowRequestUpdatesModal(false)}
        />
      )}

      {/* ----- Show Change Request Log -----  */}
      {showChangeRequestLog &&
      changeRequestData?.changeRequest?.requestHistory?.length ? (
        <ChangeRequestModal
          onClose={() => setShowChangeRequestLog(false)}
          changeRequestHistory={
            changeRequestData?.changeRequest?.requestHistory
          }
        />
      ) : null}

      <Flex flexDirection="column">
        {/* ----- Highlights ----- */}
        <Accordion
          chakraAccordionProps={{
            width: '100%',
            defaultIndex: 0,
            allowToggle: true
          }}
          items={[
            {
              title: (
                <Flex alignItems={'flex-start'} gap={2}>
                  <IconBadge icon={faBell} variant="neutral" />
                  <Flex flexDirection={'column'} marginLeft={2}>
                    <Text textStyle="headingItem">Highlights</Text>
                    <Text textStyle="textContext">
                      Please review key updates and other relevant information
                      in this phase.
                    </Text>
                  </Flex>
                </Flex>
              ),
              content: (
                <Flex flexDirection="column" gap={2}>
                  <EditorialAuthorWithdrawalRequested />

                  {(changeRequestData?.changeRequest?.requestHistory || [])
                    ?.length > 0 && (
                    <HighlightItem
                      variant="attention"
                      icon={faRefresh}
                      heading={`Awaiting ${totalRequestedChanges} Updates from Author`}
                      buttonOnClick={() => {
                        setShowChangeRequestLog(true);
                      }}
                      buttonText="View Details"
                    />
                  )}

                  <HighlightItem
                    variant="attention"
                    icon={faEdit}
                    heading={changeRequestTitle}
                    context="Select and communicate to the author any fields requiring their input."
                    customButton={
                      <ProtectedWorkflowAction actionName="EditorialSubmitChangeRequest#form">
                        <ButtonText
                          flexShrink="0"
                          onClick={() => setShowRequestUpdatesModal(true)}
                        >
                          Request Updates
                        </ButtonText>
                      </ProtectedWorkflowAction>
                    }
                  />

                  {icmjeFormList?.length &&
                    isFormConfigured(
                      submissionContext?.config?.authorICMJEModel
                    ) && (
                      <HighlightItem
                        variant="attention"
                        icon={faCopyright}
                        heading={getIcmjeDisclosureLabel(
                          icmjeFormList ?? [],
                          icmjeForm ?? []
                        )}
                        buttonOnClick={() => {
                          setActiveTab('backoffice');
                          setBackOfficeSubTab(BACKOFFICE_TABS.ICMJE_DISCLOSURE);
                        }}
                        buttonText="Review Info"
                      />
                    )}
                </Flex>
              )
            }
          ]}
        />

        {/* ----- Task Checklist ----- */}
        {(preProductionFormSchema?.formFields || []).length > 0 && (
          <Accordion
            chakraAccordionProps={{
              width: '100%',
              defaultIndex: 0,
              allowToggle: true
            }}
            items={[
              {
                title: (
                  <Flex alignItems={'flex-start'} gap={2}>
                    <IconBadge icon={faPencil} variant="neutral" />
                    <Flex flexDirection={'column'} marginLeft={2}>
                      <Text textStyle="headingItem">Task Checklist</Text>
                      <Text textStyle="textContext">
                        Ensure all required steps are completed before advancing
                        in the submission process.
                      </Text>
                    </Flex>
                  </Flex>
                ),
                content: (
                  <Flex flexDirection="column" gap={2}>
                    <form>
                      <Flex gap={4} flexDirection="column">
                        {preProductionFormSchema?.formFields?.map(
                          (field: IEditorialFormField) => renderCheckbox(field)
                        )}
                      </Flex>
                    </form>
                  </Flex>
                )
              }
            ]}
          />
        )}
      </Flex>
    </AdditionalInformationWrapper>
  );
};
